<template>
  <div id="region-page" class="container mx-auto bg-white flex flex-col px-5 py-6 sm:px-0 md:py-10">
    <section class="hero md:flex md:items-center md:justify-between md:gap-5">
      <div class="headline overflow-visible flex-1">
        <h1 class="line-clamp-1 text-2xl md:text-3xl">{{ $t('home.web.title') }}</h1>
        <h4 class="text-base mb-0 mt-2 font-400 md:text-lg">{{ regionInfo?.web_subtitle }}</h4>
      </div>
      <div class="hidden md:block md:w-278px md:h-148px md:pt-4.5">
        <etg-img src="/images/main-theme.png" alt="main-theme" class="object-contain w-full h-full" :use-webp="true" />
      </div>
    </section>
    <search-section />
    <banner-section v-if="regionInfo?.banner?.length" />
    <div class="divider" />
    <categories-list v-if="regionInfo?.cuisine?.length" />
    <horizontal-product-list v-if="isDiscountProductsShown" class="mb-5 md:mb-10" :list="discountProducts!" :title="$t('home.reserve_now.title')" />
    <brands-list v-if="regionInfo?.brand?.length" />
    <themes-list v-if="regionInfo?.theme?.length" />
    <locations-list v-if="regionInfo?.location?.length" />
    <horizontal-product-list v-if="popularProducts?.length" class="mb-8 md:mb-12" :list="popularProducts" :title="$t('home.popular.title')" />
    <information-section />
    <other-cities-list v-if="otherCities?.length" :list="otherCities" />
  </div>
</template>

<script setup lang="ts">
import SearchSection from '@/components/home/search-section.vue'
import BannerSection from '@/components/home/banner-section.vue'
import CategoriesList from '@/components/home/categories-list.vue'
import ThemesList from '@/components/home/themes-list.vue'
import BrandsList from '@/components/home/brands-list.vue'
import HorizontalProductList from '@/components/home/horizontal-product-list.vue'
import LocationsList from '@/components/home/locations-list.vue'
import InformationSection from '@/components/home/information-section.vue'
import OtherCitiesList from '@/components/home/other-cities-list.vue'

import { useAppStore } from '@/store/app'
import { useRegionStore } from '@/store/region'
import { useBranchStore } from '@/store/branch'
import { GtmEvent } from '@/types/gtm'
import { AsyncDataKeys } from '@/constants/async-data-keys'

import { getRegionPopular, getRegionDiscount, getRegionOtherCities } from '@/service/region'

const DISCOUNT_PRODUCTS_DISPLAY_THRESHOLD = 4

const nuxtApp = useNuxtApp()

const appStore = useAppStore()
const regionStore = useRegionStore()
const branchStore = useBranchStore()

const { regionInfo } = storeToRefs(regionStore)

const route = useRoute()
const { regionId } = useRegion()

const discountProducts = computed(() => branchStore.listIds?.discountProducts || [])
const popularProducts = computed(() => branchStore.listIds?.popularProducts || [])
const isDiscountProductsShown = computed(() => discountProducts.value.length >= DISCOUNT_PRODUCTS_DISPLAY_THRESHOLD)
const seoSettingName = computed(() => (checkIfRegionPage(route.name) ? 'region' : null))

useEtgSeoSetting(seoSettingName.value, { regionInfo })

useAsyncData(
  useAsyncDataKey(AsyncDataKeys.REGION_DISCOUNT_PRODUCTS, [regionId.value]),
  async () => {
    if (typeof regionId.value !== 'number') {
      return []
    }

    const {
      data: { list: discountProducts },
    } = await getRegionDiscount(regionId.value)

    branchStore.setBranches('discountProducts', discountProducts)

    return discountProducts
  },
  { watch: [regionId], server: false, dedupe: 'cancel' }
)

useAsyncData(
  useAsyncDataKey(AsyncDataKeys.REGION_POPULAR_PRODUCTS, [regionId.value]),
  async () => {
    if (typeof regionId.value !== 'number') {
      return []
    }

    const {
      data: { list: popularProducts },
    } = await getRegionPopular(regionId.value, appStore.geolocation)

    branchStore.setBranches('popularProducts', popularProducts)

    return popularProducts
  },
  { watch: [regionId], server: false, dedupe: 'cancel' }
)

onMounted(() => {
  nuxtApp.$trackEvent(GtmEvent.EXPLORE_TAB_VIEWED)
})

const { data: otherCities } = useAsyncData(
  useAsyncDataKey(AsyncDataKeys.REGION_OTHER_CITIES, [regionId.value]),
  async () => {
    if (typeof regionId.value !== 'number') {
      return []
    }

    const {
      data: { list: otherCities },
    } = await getRegionOtherCities(regionId.value, appStore.geolocation)

    return otherCities
  },
  { watch: [regionId], server: false, dedupe: 'cancel' }
)
</script>

<style lang="scss" scoped>
.divider {
  @apply bg-gray-40 my-4 w-full h-1px md:my-6;
}
</style>
